"use client"

/** THE PAGE STOPPED RELOADING ON 4005 (VC-Authz's E2): the server kept
 *  closing this tab's sockets as "organisation changed", so after
 *  RELOADS_ALLOWED reloads within a minute the page stopped and asks. What
 *  it asks depends on why the server closed it: a switch whose answer never
 *  arrived (switching again, even to the same organisation, settles it), or
 *  a session the server could not read just then. */

import React, { useEffect, useState } from "react";
import {
  onOrganisationChangeStuck, organisationChangeStuck, type OrganisationChangeStuck,
} from "@sourcepilot/ui/framework/data/dag-sync/organisation-change";
import { getServingIdentity } from "@sourcepilot/ui/framework/data/dag-sync/serving-identity";
import { switchOrganisation } from "@sourcepilot/ui/framework/data/switch-organisation";
import { performLogout } from "./use-simple-auth";

export function useOrganisationChangeStuck(): OrganisationChangeStuck | null {
  const [reason, setReason] = useState<OrganisationChangeStuck | null>(organisationChangeStuck);
  useEffect(() => {
    setReason(organisationChangeStuck());
    return onOrganisationChangeStuck(setReason);
  }, []);
  return reason;
}

const reload = () => window.location.reload();

/** the organisation the session acts as, as this page knows it */
function currentOrganisation(): string {
  const identity = getServingIdentity() ?? "";
  return identity.slice(identity.lastIndexOf("|") + 1);
}

export function OrganisationChangeStuckNotice() {
  const reason = useOrganisationChangeStuck();
  const [switching, setSwitching] = useState(false);
  const [failed, setFailed] = useState(false);
  if (!reason) return null;
  const button = "rounded border border-amber-300 bg-white px-2 py-0.5 text-xs hover:bg-amber-100 disabled:opacity-50";
  const switchAgain = async () => {
    const org = currentOrganisation();
    if (!org) { window.location.href = "/organisations"; return; }
    setSwitching(true);
    setFailed(false);
    if (!(await switchOrganisation(org))) { setSwitching(false); setFailed(true); }
  };
  return (
    <div role="alert" data-testid="organisation-change-stuck" data-reason={reason}
      className="flex flex-wrap items-center justify-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      {reason === "session_unverifiable" ? (
        <>
          <span>We can't confirm your session right now.</span>
          <button className={button} onClick={reload} data-testid="organisation-change-stuck-retry">Retry</button>
          <button className={button} onClick={() => void performLogout()} data-testid="organisation-change-stuck-sign-in">Sign in again</button>
        </>
      ) : (
        <>
          <span>Your organisation was changed in another tab or the change didn't finish.</span>
          <button className={button} onClick={reload} data-testid="organisation-change-stuck-reload">Reload</button>
          <button className={button} onClick={() => void switchAgain()} disabled={switching} data-testid="organisation-change-stuck-switch">Switch organisation</button>
          {failed && <span className="text-xs" data-testid="organisation-change-stuck-switch-failed">The switch didn't go through. Try again, or reload.</span>}
        </>
      )}
    </div>
  );
}
